import { Button, ButtonProps, Heading, Icon, Modal } from "@ttc3k/trekker";
import {
  ForwardRefExoticComponent,
  PropsWithChildren,
  ReactNode,
  RefAttributes,
  SVGProps,
} from "react";
import { Box, Flex, Grid, GridItem } from "styled-system/jsx";
import { useTranslation } from "react-i18next";

type SplitViewModalProps = PropsWithChildren & {
  isOpen: boolean;
  onClose: () => void;
  HeaderIcon?: ForwardRefExoticComponent<
    Omit<SVGProps<SVGSVGElement>, "ref"> & RefAttributes<SVGSVGElement>
  >;
  title?: string;
  closeButtonProps?: ButtonProps;
  saveButtonProps?: ButtonProps;
  sidebar: ReactNode;
};

export const SplitViewModal = ({
  isOpen,
  onClose,
  children,
  HeaderIcon,
  title,
  closeButtonProps,
  saveButtonProps,
  sidebar,
}: SplitViewModalProps) => {
  const { t } = useTranslation();
  return (
    <Modal
      open={isOpen}
      placement={"centerPane"}
      onOpenChange={onClose}
      hideCloseTrigger
      noMaxH
      portal
    >
      <Box
        bg={"bg.wa.light"}
        w={{
          base: "full",
          md: "860px",
          lg: "1100px",
        }}
        h={"full"}
        minH={"780px"}
      >
        {/* Header */}
        <Flex
          bg={"white"}
          px={"400"}
          py={"300"}
          gap={"200"}
          borderBottomWidth={"1px"}
          borderBottomColor={"border.light"}
          borderBottomStyle={"solid"}
          alignItems={"center"}
          justifyContent={"space-between"}
        >
          <Flex alignItems={"center"} gap={"150"}>
            {HeaderIcon && <Icon Element={HeaderIcon} />}
            <Heading size={"h5"} color={"text.dark"}>
              {title}
            </Heading>
          </Flex>
          <Flex alignItems={"center"} gap={"150"}>
            <Button
              children={t("core:MODAL_BUTTON_COPY.CLOSE")}
              visual={"primary"}
              mode={"light"}
              onClick={onClose}
              {...closeButtonProps}
            />
            <Button
              children={t("core:MODAL_BUTTON_COPY.SAVE")}
              visual={"accent.blue"}
              {...saveButtonProps}
            />
          </Flex>
        </Flex>

        {/* Content */}
        <Grid
          gridTemplateColumns={{ base: "repeat(2, 1fr)", md: "1fr 2fr" }}
          h={"full"}
        >
          <GridItem
            // Note* Don't include the padding for the sidebar, allow to caller
            // to set it
            bg={"bg.wa.lightest"}
            borderRightColor={"border.light"}
            borderRightWidth={"1px"}
            borderRightStyle={"solid"}
            // Height equals whichever is the smallest: either 780px or 100vh
            // minus the modal header height
            h={"min(780px, calc(100vh - 96px))"}
            // Allow the caller to manage the scroll
            overflow={"hidden"}
          >
            {sidebar}
          </GridItem>
          <GridItem
            py={"500"}
            px={"400"}
            h={"min(780px, calc(100vh - 96px))"}
            overflowY={"auto"}
          >
            {children}
          </GridItem>
        </Grid>
      </Box>
    </Modal>
  );
};
